import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Link } from "react-router-dom";
import { Loader2, LayoutDashboard, AlertTriangle, Activity } from "lucide-react";
import type { DataQualityStatus } from "@/api/intelligence";
import { useAuth } from "@/contexts/AuthContext";
import { intelligenceApi, type IntelligencePeriodPreset } from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import {
  CommandCentreFilters,
  type IntelligencePeriodFilterValue,
} from "@/components/intelligence/CommandCentreFilters";
import { KpiCard } from "@/components/intelligence/KpiCard";
import { RevenuePerformanceSection } from "@/components/intelligence/RevenuePerformanceSection";
import { PatientFunnelSection } from "@/components/intelligence/PatientFunnelSection";
import { HospitalComparisonTable } from "@/components/intelligence/HospitalComparisonTable";
import { DomainSummariesSection } from "@/components/intelligence/DomainSummariesSection";
import { IntelligenceExportButton } from "@/components/intelligence/IntelligenceExportButton";
import { IntelligenceServerExportButton } from "@/components/intelligence/IntelligenceServerExportButton";
import { ManagementAttentionSection } from "@/components/intelligence/ManagementAttentionSection";
import { buildCommandCentreExportRows } from "@/lib/intelligenceExport";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { CommandCentreFindingsSection } from "@/components/intelligence/CommandCentreFindingsSection";
import { BenchmarkSummarySection } from "@/components/intelligence/BenchmarkSummarySection";
import { Card, CardContent } from "@/components/ui/card";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function freshnessCardClass(status: string): string {
  if (status === "ERROR") return "border-red-200 bg-red-50/80";
  if (status === "STALE" || status === "WARNING") return "border-amber-200 bg-amber-50/80";
  return "border-slate-200 bg-slate-50/80";
}

function formatFreshnessWhen(iso: string | null): string {
  if (!iso) return "never";
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  } catch {
    return iso;
  }
}

function worstFreshnessStatus(...statuses: DataQualityStatus[]): DataQualityStatus {
  const rank: Record<DataQualityStatus, number> = {
    HEALTHY: 0,
    WARNING: 1,
    STALE: 2,
    ERROR: 3,
  };
  return statuses.reduce((a, b) => (rank[b] > rank[a] ? b : a), "HEALTHY");
}

const EXECUTIVE_KEYS = [
  "gross_billing",
  "net_revenue",
  "collections",
  "outstanding",
  "new_patients",
  "opd_visits",
  "ipd_admissions",
  "leads",
] as const;

export default function IntelligenceCommandCentrePage() {
  const { user, activeHospitalId, isChainAdmin } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const isInvestor = user?.role === "INVESTOR";
  const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
  const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;

  const chainHospitals = user?.hospitals ?? [];
  const isChainScoped = isChainAdmin || investorHasChainScope;
  const defaultFinancialHospitalId = useMemo(
    () =>
      defaultFinancialReportHospitalId({
        isSuperAdmin,
        isChainScoped,
        activeHospitalId,
        fallbackHospitalId: chainHospitals[0]?.id ?? null,
      }),
    [isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
  );

  const [period, setPeriod] = useState<IntelligencePeriodFilterValue>({
    preset: "this_month",
  });

  const [selectedHospitalId, setSelectedHospitalId] = useState<string>("");

  useEffect(() => {
    if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
      setSelectedHospitalId(defaultFinancialHospitalId || "all");
    }
  }, [defaultFinancialHospitalId, selectedHospitalId]);

  const { data: superHospitals } = useQuery({
    queryKey: ["super-admin-hospitals-intelligence"],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: isSuperAdmin,
  });

  const hospitalOptions = useMemo(() => {
    if (isSuperAdmin && superHospitals?.length) {
      return superHospitals.map((h) => ({ id: h.id, name: h.name }));
    }
    if (isChainScoped && chainHospitals.length) {
      return chainHospitals.map((h) => ({ id: h.id, name: h.name }));
    }
    if (user?.hospitalId) {
      const name =
        chainHospitals.find((h) => h.id === user.hospitalId)?.name ?? "My Hospital";
      return [{ id: user.hospitalId, name }];
    }
    return [];
  }, [isSuperAdmin, isChainScoped, superHospitals, chainHospitals, user?.hospitalId]);

  const reportHospitalId = useMemo(
    () =>
      intelligenceReportHospitalId({
        showHospitalFilter,
        selectedHospitalId,
        isChainScoped,
      }),
    [showHospitalFilter, selectedHospitalId, isChainScoped]
  );

  const customReady =
    period.preset !== "custom" || (!!period.from && !!period.to && period.from <= period.to);

  const queryParams = {
    period: period.preset as IntelligencePeriodPreset,
    from: period.preset === "custom" ? period.from : undefined,
    to: period.preset === "custom" ? period.to : undefined,
    hospitalId: reportHospitalId,
    compare: true,
  };

  const {
    data,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: [
      "intelligence-command-centre",
      period.preset,
      period.from,
      period.to,
      reportHospitalId ?? "scoped",
    ],
    queryFn: () => intelligenceApi.getCommandCentre(queryParams),
    enabled: customReady,
    staleTime: 60_000,
  });

  const executiveKpis = EXECUTIVE_KEYS.map((key) => data?.kpis.find((k: { key: string }) => k.key === key)).filter(
    Boolean
  );

  const freshness = data?.dataQuality.freshness;
  const freshnessIssue =
    freshness &&
    (freshness.aggregationStatus !== "HEALTHY" ||
      freshness.detectionStatus !== "HEALTHY" ||
      freshness.missingDailyFactsLast30 > 0);
  const isAdmin = isSuperAdmin || user?.role === "HOSPITAL_ADMIN";
  const dataQualityPath = `${isSuperAdmin ? "/super-admin" : "/hospital-admin"}/intelligence/data-quality`;
  const worstStatus = freshness
    ? worstFreshnessStatus(
        freshness.aggregationStatus as DataQualityStatus,
        freshness.detectionStatus as DataQualityStatus
      )
    : "HEALTHY";

  return (
    <div className="space-y-6 pb-10">
      <IntelligenceSubNav basePath={isSuperAdmin ? "/super-admin" : "/hospital-admin"} />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-indigo-700">
            <LayoutDashboard className="h-6 w-6" />
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Command Centre</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Management cockpit · validated Intelligence KPIs
            {data?.scope.hospitalName ? ` · ${data.scope.hospitalName}` : ""}
          </p>
        </div>
        {isFetching && !isLoading && (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Updating…
          </span>
        )}
        {data && customReady && (
          <div className="flex flex-wrap gap-2">
            <IntelligenceExportButton
              filename={`command-centre-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
              buildRows={() => buildCommandCentreExportRows(data)}
              label="Quick CSV"
            />
            <IntelligenceServerExportButton
              report="command-centre"
              params={queryParams}
              filename={`command-centre-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
            />
          </div>
        )}
      </div>

      <CommandCentreFilters
        hospitals={hospitalOptions}
        showHospitalFilter={showHospitalFilter}
        hospitalId={selectedHospitalId}
        onHospitalChange={setSelectedHospitalId}
        period={period}
        onPeriodChange={setPeriod}
      />

      {!customReady && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="pt-4 text-sm">
            <p className="font-medium text-amber-900">Select a valid custom date range</p>
            <p className="text-amber-800/80 mt-1">Both from and to dates are required (from ≤ to).</p>
          </CardContent>
        </Card>
      )}

      {isLoading && customReady && (
        <div className="flex items-center justify-center py-24 text-muted-foreground gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading Command Centre…
        </div>
      )}

      {isError && customReady && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="pt-4 text-sm">
            <p className="font-medium text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Unable to load Command Centre
            </p>
            <p className="text-muted-foreground mt-1">
              {(error as Error)?.message ?? "Check your access permissions and try again."}
            </p>
          </CardContent>
        </Card>
      )}

      {data && customReady && (
        <>
          {data.dataQuality.emptyPeriod && (
            <Card className="border-slate-200 bg-slate-50/80">
              <CardContent className="pt-4 text-sm text-muted-foreground">
                No activity recorded for {data.period.label}. This may reflect an empty database
                or a quiet period.
              </CardContent>
            </Card>
          )}

          {freshnessIssue && (
            <Card className={freshnessCardClass(worstStatus)}>
              <CardContent className="pt-4 text-sm space-y-2">
                <div className="flex items-start gap-2">
                  <Activity className="h-4 w-4 shrink-0 mt-0.5 text-amber-800" />
                  <div className="space-y-1">
                    <p className="font-medium text-slate-900">Intelligence pipeline freshness</p>
                    <p className="text-muted-foreground">
                      Daily aggregation: <strong>{freshness.aggregationStatus}</strong>
                      {freshness.lastAggregationAt
                        ? ` · last success ${formatFreshnessWhen(freshness.lastAggregationAt)} IST`
                        : " · no successful run recorded"}
                    </p>
                    <p className="text-muted-foreground">
                      Detection: <strong>{freshness.detectionStatus}</strong>
                      {freshness.lastDetectionAt
                        ? ` · last success ${formatFreshnessWhen(freshness.lastDetectionAt)} IST`
                        : " · no successful run recorded"}
                    </p>
                    {freshness.missingDailyFactsLast30 > 0 && (
                      <p className="text-muted-foreground">
                        Missing daily rollup days (last 30): {freshness.missingDailyFactsLast30}
                      </p>
                    )}
                    {isAdmin && (
                      <Link to={dataQualityPath} className="text-indigo-700 hover:underline text-sm">
                        View full data quality report →
                      </Link>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-900">Executive KPIs</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
              {executiveKpis.map((kpi) => (
                <KpiCard key={kpi!.key} kpi={kpi!} comparisonLabel={data.comparisonPeriod?.label} />
              ))}
            </div>
          </section>

          <RevenuePerformanceSection data={data} />
          <DomainSummariesSection
            summaries={data.domainSummaries}
            basePath={isSuperAdmin ? "/super-admin" : "/hospital-admin"}
            comparisonLabel={data.comparisonPeriod?.label}
          />
          <PatientFunnelSection data={data} />
          <HospitalComparisonTable data={data} />
          {data.benchmarkSummary && (
            <BenchmarkSummarySection
              items={data.benchmarkSummary.items}
              limitations={data.benchmarkSummary.limitations}
              enabled={data.benchmarkSummary.enabled}
              basePath={isSuperAdmin ? "/super-admin" : "/hospital-admin"}
            />
          )}
          <ManagementAttentionSection
            data={data}
            basePath={isSuperAdmin ? "/super-admin" : "/hospital-admin"}
          />
          <CommandCentreFindingsSection
            data={data}
            basePath={isSuperAdmin ? "/super-admin" : "/hospital-admin"}
          />
        </>
      )}
    </div>
  );
}
